import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import { getAccountOwnerId } from 'redux/selectors/accounts';
import { getProjectVariantMap } from 'redux/selectors/projects';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import type { LightUser } from 'types/user';
import { naturalCompare, naturalSortBy } from 'utils/naturalSort';
import { keyBy, notNull, sortBy, uniqBy } from 'utils/typesafe';

const readUsers = (state: IState) => state.users.entries;
const readSearchResults = (state: IState) => state.users.searchResults;
const readXhrStatus = (state: IState) => state.users.xhrStatus;

export const getUsersXhrStatus = createSelector([readXhrStatus], xhrStatus =>
  xhrStatusUIState(xhrStatus)
);
export const getIsUsersSaving = createSelector(
  [getUsersXhrStatus],
  xhrStatus => xhrStatus.isSaving
);
export const getIsLoadingUsers = createSelector(
  [getUsersXhrStatus],
  xhrStatus => xhrStatus.isLoading
);

export const getUsers = readUsers;

export const getUserEntities = createSelector([getUsers], users => keyBy(users, 'id'));

// Import this into other selectors as needed instead of reading state.users.entries directly.
// This'll allow easier transition to normalized state.
export const getAllUsers = createSelector([getUsers], users =>
  users.slice().sort((a, b) => naturalCompare(a.name, b.name))
);

export const getAccountOwner = createSelector(
  [getAllUsers, getAccountOwnerId],
  (users, ownerUserId) => users.find(u => u.id === ownerUserId) || null
);

export const getNonDeletedUsers = createSelector([getAllUsers], users =>
  users.filter(u => !u.deleted)
);

export const getNonDeletedUserIds = createSelector([getNonDeletedUsers], users =>
  users.map(u => u.id)
);

export const getDeletedUsers = createSelector([getAllUsers], users => users.filter(u => u.deleted));

// Current members who have accepted their invite. Matches "Active" on the Subscription page.
export const getActiveUsers = createSelector([getNonDeletedUsers], users =>
  users.filter(u => u.active)
);

// Current members whose invite is still pending. Matches "Invited" on the Subscription page.
export const getInvitedUsers = createSelector([getNonDeletedUsers], users =>
  users.filter(u => !u.active)
);

export const getUserSearchProps = createSelector(
  [
    (
      state: IState,
      props: { forProjectId?: number; initialUserId?: number | null; includeDeleted?: boolean }
    ) => props,
  ],
  props => props
);

export interface UserDropdownValue {
  value: number;
  label: string;
  avatar: string | null;
  email: string | null;
  deleted?: boolean;
  empty?: boolean;
}

export const makeGetUserSearchOptions = () =>
  createSelector(
    [getAllUsers, getUserEntities, getProjectVariantMap, getUserSearchProps],
    (users, userEntities, projects, componentProps): Array<UserDropdownValue> => {
      const projectById = componentProps.forProjectId
        ? projects[componentProps.forProjectId]
        : null;

      const initialUser = componentProps.initialUserId
        ? userEntities[componentProps.initialUserId]
        : null;
      let userIsOnProject = false;

      if (projectById && projectById.type === 'full' && projectById.project.users?.length) {
        const usersToReturn = projectById.project.users
          .map(projectUser => {
            const user = userEntities?.[projectUser?.user_id];
            if (user == null) {
              return null;
            }
            if (!componentProps.includeDeleted && user.deleted) {
              return null;
            }
            if (initialUser && user.id === initialUser.id) {
              userIsOnProject = true;
            }
            return {
              value: user.id,
              label: user.name,
              avatar: user.avatar ? user.avatar.medium_retina : null,
              email: user.email,
              deleted: user.deleted,
            };
          })
          .filter(notNull);

        if (initialUser && !userIsOnProject) {
          usersToReturn.push({
            value: initialUser.id,
            label: initialUser.name,
            avatar: initialUser.avatar ? initialUser.avatar.medium_retina : null,
            email: initialUser.email,
            deleted: initialUser.deleted,
          });
        }
        return naturalSortBy(usersToReturn, user => user?.label);
      }
      return users
        .map(user => {
          if (!componentProps.includeDeleted && user.deleted) {
            return null;
          }
          return {
            value: user.id,
            label: user.name,
            avatar: user.avatar ? user.avatar.medium_retina : null,
            email: user.email,
            deleted: user.deleted,
          };
        })
        .filter(notNull);
    }
  );

export const getAccountAdmins = createSelector([getAllUsers], users =>
  users.filter(user => user.admin && !user.deleted)
);

export const getNonDeletedUsersWithSearchResults = createSelector(
  [getNonDeletedUsers, readSearchResults],
  (users, searchResults): Array<LightUser> =>
    sortBy(
      uniqBy([...(users as Array<LightUser>), ...searchResults], user => user.id),
      user => user.name
    )
);
